import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  animation?: 'up' | 'fade' | 'scale';
  delayMs?: number;
  durationMs?: number;
  threshold?: number;
  once?: boolean;
}

/**
 * High-performance, zero-dependency scroll reveal component.
 * Uses GPU-accelerated CSS transforms and IntersectionObserver.
 * Unobserves after reveal to keep memory and CPU usage at zero.
 */
export const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className,
  animation = 'up',
  delayMs = 0,
  durationMs = 600,
  threshold = 0.1,
  once = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once && ref.current) {
            observer.unobserve(ref.current);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -30px 0px',
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold, once]);

  const getTransform = () => {
    if (isVisible) return 'translate3d(0, 0, 0) scale(1)';
    switch (animation) {
      case 'up':
        return 'translate3d(0, 20px, 0)';
      case 'scale':
        return 'scale(0.97)';
      case 'fade':
      default:
        return 'translate3d(0, 0, 0)';
    }
  };

  const style: React.CSSProperties = {
    transitionProperty: 'opacity, transform',
    transitionDuration: `${durationMs}ms`,
    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
    transitionDelay: `${delayMs}ms`,
    willChange: isVisible ? 'auto' : 'opacity, transform',
    opacity: isVisible ? 1 : 0,
    transform: getTransform(),
  };

  return (
    <div ref={ref} style={style} className={cn(className)}>
      {children}
    </div>
  );
};
